import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BackendLogsService } from '../../../../services/backend-logs.service';
import {
  BackendLog,
  BackendLogFilter,
  BackendLogLevel,
  BackendLogStats,
} from '../../../../interfaces/backend-log.interface';
import { BackendLogDetailDialogComponent } from '../backend-log-detail-dialog/backend-log-detail-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../shared/components/confirm-dialog/confirm-dialog';
import { DeleteIconButton } from '../../../../shared/components/delete-icon-button';

const ALL_LEVELS: BackendLogLevel[] = ['WARNING', 'ERROR', 'CRITICAL'];

@Component({
  selector: 'app-backend-logs-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    DeleteIconButton,
  ],
  templateUrl: './backend-logs-panel.component.html',
  styleUrls: ['./backend-logs-panel.component.scss'],
})
export class BackendLogsPanelComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  // Adatok
  logs: BackendLog[] = [];
  total = 0;
  stats: BackendLogStats | null = null;

  // Állapot
  isLoading = true;
  errorMessage: string | null = null;
  lastRefresh: Date | null = null;

  // Pagination
  page = 1;
  pageSize = 20;
  pageSizeOptions = [10, 20, 50, 100];

  // Szűrők
  selectedLevels: Set<BackendLogLevel> = new Set(ALL_LEVELS);
  searchTerm = '';

  // Belső
  private searchInput$ = new Subject<string>();
  readonly availableLevels = ALL_LEVELS;

  constructor(
    private logsService: BackendLogsService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Search debounce
    this.searchInput$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.page = 1;
        this.loadLogs();
      });

    // Kezdeti betöltés
    this.loadLogs();
    this.loadStats();
  }

  loadLogs(silent = false): void {
    if (!silent) {
      this.isLoading = true;
    }
    const filter: BackendLogFilter = {
      page: this.page,
      page_size: this.pageSize,
      level: Array.from(this.selectedLevels),
      search: this.searchTerm.trim() || undefined,
    };

    this.logsService.getLogs(filter).subscribe({
      next: (response) => {
        this.logs = response.items;
        this.total = response.total;
        this.lastRefresh = new Date();
        this.isLoading = false;
        this.errorMessage = null;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.errorMessage = err?.message || 'Logok betöltése sikertelen';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  loadStats(): void {
    this.logsService.getStats().subscribe({
      next: (stats) => {
        this.stats = stats;
        this.cdr.markForCheck();
      },
      error: () => {
        // Stats hiba nem kritikus, csendben átengedjük
      },
    });
  }

  toggleLevel(level: BackendLogLevel): void {
    if (this.selectedLevels.has(level)) {
      // Legalább egynek mindig kiválasztva kell lennie
      if (this.selectedLevels.size === 1) {
        return;
      }
      this.selectedLevels.delete(level);
    } else {
      this.selectedLevels.add(level);
    }
    this.page = 1;
    this.loadLogs();
  }

  isLevelActive(level: BackendLogLevel): boolean {
    return this.selectedLevels.has(level);
  }

  onSearchChange(value: string): void {
    this.searchInput$.next(value);
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadLogs();
  }

  refresh(): void {
    this.loadLogs();
    this.loadStats();
  }

  openDetail(log: BackendLog): void {
    const ref = this.dialog.open(BackendLogDetailDialogComponent, {
      data: { logId: log.id },
      width: '860px',
      maxWidth: '95vw',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.deleted) {
        this.loadLogs();
        this.loadStats();
      }
    });
  }

  deleteAll(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Összes log törlése',
        message: 'Biztosan törölni szeretnéd az összes backend log bejegyzést? Ez a művelet nem vonható vissza.',
      } as ConfirmDialogData,
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.logsService.deleteAllLogs().subscribe({
        next: () => {
          this.loadLogs();
          this.loadStats();
        },
        error: () => {},
      });
    });
  }

  trackByLogId(_idx: number, log: BackendLog): number {
    return log.id;
  }

  getLevelClass(level: BackendLogLevel): string {
    return 'level-' + level.toLowerCase();
  }

  truncateMessage(msg: string, maxLen = 120): string {
    if (!msg) return '';
    return msg.length > maxLen ? msg.substring(0, maxLen) + '…' : msg;
  }
}
